import React, { useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import {
  BarChart as ChartIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { INK } from './vizTokens';

// The shell every cohort chart renders inside.
//
// It exists to make three things structural rather than optional:
//
//  1. The title states the finding, not the data source. "Revenue by quarter"
//     is a filing label; "Revenue grew 18% in Q3" is a chart. Callers pass a
//     computed `insight` string.
//  2. Every chart has a table-view twin. A colour scale is not readable by
//     everyone, and a tooltip is not a way to publish a value.
//  3. Missing data gets an explicit empty state instead of rendering as zero.
//     Blank bars for unreviewed learners are how the old Understanding Pattern
//     chart implied a score of nought for people who simply had not submitted.

export const ChartFrame = ({
  question,
  insight,
  footnote,
  tableHead = [],
  tableRows = [],
  emptyMessage = '',
  height,
  children,
}) => {
  const [showTable, setShowTable] = useState(false);
  const hasTable = tableHead.length > 0 && tableRows.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          {question && (
            <Typography
              variant="overline"
              sx={{ color: INK.muted, fontWeight: 700, letterSpacing: '0.06em', display: 'block', lineHeight: 1.6 }}
            >
              {question}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: INK.primary, lineHeight: 1.4 }}>
            {insight}
          </Typography>
        </Box>
        {hasTable && (
          <MuiTooltip title={showTable ? 'Show chart' : 'Show data table'}>
            <IconButton
              size="small"
              onClick={() => setShowTable((prev) => !prev)}
              aria-label={showTable ? 'Show chart' : 'Show data table'}
            >
              {showTable ? <ChartIcon fontSize="small" /> : <TableIcon fontSize="small" />}
            </IconButton>
          </MuiTooltip>
        )}
      </Stack>

      <Box sx={{ mt: 2, flexGrow: 1, minWidth: 0 }}>
        {emptyMessage ? (
          <Alert severity="info" sx={{ mt: 1 }}>{emptyMessage}</Alert>
        ) : showTable ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {tableHead.map((head) => (
                    <TableCell key={head} sx={{ fontWeight: 800, color: INK.secondary, whiteSpace: 'nowrap' }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((cells, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`row-${index}`}>
                    {cells.map((cell, cellIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableCell key={`cell-${cellIndex}`} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ height, width: '100%' }}>{children}</Box>
        )}
      </Box>

      {footnote && !emptyMessage && (
        <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 1.5, lineHeight: 1.6 }}>
          {footnote}
        </Typography>
      )}
    </Paper>
  );
};

// Shared tooltip body. Recharts' default renders raw dataKeys, which leaks
// field names like `avg_confidence` into the UI.
export const VizTooltip = ({ title, rows = [], note }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.5,
      py: 1.25,
      border: `1px solid ${INK.grid}`,
      borderRadius: 1.5,
      bgcolor: INK.surface,
      maxWidth: 260,
    }}
  >
    <Typography variant="body2" fontWeight={800} sx={{ color: INK.primary, mb: rows.length ? 0.75 : 0 }}>
      {title}
    </Typography>
    <Stack spacing={0.4}>
      {rows.map((row) => (
        <Stack key={row.label} direction="row" spacing={2} justifyContent="space-between">
          <Typography variant="caption" sx={{ color: INK.secondary }}>{row.label}</Typography>
          <Typography variant="caption" fontWeight={800} sx={{ color: INK.primary, fontVariantNumeric: 'tabular-nums' }}>
            {row.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
    {note && (
      <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 0.75, lineHeight: 1.5 }}>
        {note}
      </Typography>
    )}
  </Paper>
);

export default ChartFrame;
